/*
 * EmployeeVideoBlocker — modal fullscreen com vídeo de comunicação. O CIENTE
 * só é liberado quando o funcionário assiste ao vídeo até o fim (100%).
 * Estratégia anti-skip:
 *   - watchedTo = maior currentTime já alcançado durante playback natural.
 *   - Se o usuário tenta seek FRENTE além de watchedTo, resetamos currentTime.
 *   - Seek para TRÁS é livre (assistir de novo é OK).
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Button } from "./ui/button";
import { CheckCircle2, Loader2, ShieldCheck, Film, Lock } from "lucide-react";

export default function EmployeeVideoBlocker({ onAllDone }) {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [current, setCurrent] = useState(0);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/employee/videos/pending`, {
        params: { token }, timeout: 15000,
      });
      const list = r.data?.items || [];
      setItems(list);
      setCurrent(0);
      if (list.length === 0) onAllDone?.();
    } catch {
      // Fail open: never block the portal because of a network hiccup.
      setItems([]);
      onAllDone?.();
    } finally { setLoading(false); }
  }, [token, onAllDone]);

  useEffect(() => { load(); }, [load]);

  const ack = async () => {
    const v = items[current];
    if (!v) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/employee/videos/${v.id}/acknowledge`,
        { token }, { timeout: 15000 });
      if (current + 1 >= items.length) {
        onAllDone?.();
        setItems([]);
      } else {
        setCurrent((c) => c + 1);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao registrar Ciente.");
    } finally { setSubmitting(false); }
  };

  if (loading || items.length === 0) return null;

  const v = items[current];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-slate-900/90 backdrop-blur flex flex-col items-center justify-center p-4"
        data-testid="video-blocker"
      >
        <VideoCard
          key={v.id}
          video={v}
          token={token}
          items={items}
          current={current}
          submitting={submitting}
          onCiente={ack}
        />
      </motion.div>
    </AnimatePresence>
  );
}

function VideoCard({ video, token, items, current, submitting, onCiente }) {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [watchedTo, setWatchedTo] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [ended, setEnded] = useState(false);

  const percent = duration > 0
    ? Math.min(100, Math.round((watchedTo / duration) * 100))
    : 0;
  const canAck = ended || (duration > 0 && watchedTo >= duration - 1);

  const onMeta = () => {
    const el = videoRef.current;
    if (el && Number.isFinite(el.duration)) setDuration(el.duration);
  };

  const onTime = () => {
    const el = videoRef.current;
    if (!el) return;
    const t = el.currentTime;
    setCurrentTime(t);
    // Only bump watchedTo during "natural" playback (not during seeking or paused)
    if (!el.paused && !el.seeking) {
      setWatchedTo((prev) => (t > prev ? t : prev));
    }
  };

  const onSeeking = () => {
    const el = videoRef.current;
    if (!el) return;
    // Allow rewinding (seek < watchedTo). Block forward seeks beyond
    // the furthest naturally-reached point.
    if (el.currentTime > watchedTo + 0.75) {
      el.currentTime = watchedTo;
    }
  };

  const onEndedFn = () => {
    setEnded(true);
    const el = videoRef.current;
    if (el) setWatchedTo(el.duration || watchedTo);
  };

  const fmtT = (t) => {
    if (!Number.isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-blue-50">
        <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wider flex items-center gap-1.5">
          <Film className="w-3.5 h-3.5" />
          Vídeo de Comunicação {items.length > 1
            ? `${current + 1}/${items.length}` : ""}
        </p>
        <h2 className="text-base sm:text-lg font-heading font-bold text-slate-900">
          {video.title || video.filename}
        </h2>
      </div>

      <div className="bg-slate-900 flex items-center justify-center">
        <video
          ref={videoRef}
          src={`${API}/employee/videos/${video.id}/stream?token=${encodeURIComponent(token || "")}`}
          controls
          playsInline
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          onLoadedMetadata={onMeta}
          onDurationChange={onMeta}
          onTimeUpdate={onTime}
          onSeeking={onSeeking}
          onEnded={onEndedFn}
          className="w-full max-h-[60vh] bg-black"
          data-testid={`video-player-${video.id}`}
        />
      </div>

      {/* Progress + gating */}
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
        <div className="flex items-center justify-between text-[11px] text-slate-600 font-mono mb-1">
          <span>Assistido: {fmtT(watchedTo)} / {fmtT(duration)}</span>
          <span className="font-semibold" data-testid="video-progress-pct">
            {percent}%
          </span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <motion.div
            className={`h-full ${canAck ? "bg-emerald-500" : "bg-indigo-500"}`}
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 0.2 }}
            data-testid="video-progress-bar"
          />
        </div>
        {!canAck && (
          <p className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Você precisa assistir ao vídeo inteiro para dar Ciente. Não é possível avançar.
          </p>
        )}
      </div>

      <div className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-50 border-t border-slate-100">
        <p className="text-xs text-slate-600 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          Ao clicar em Ciente você confirma que assistiu ao vídeo por completo. Seu Ciente fica registrado com data, hora e IP.
        </p>
        <Button
          onClick={onCiente}
          disabled={submitting || !canAck}
          size="lg"
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-lg min-w-[160px] disabled:bg-slate-400 disabled:opacity-70"
          data-testid={`video-ack-btn-${video.id}`}
        >
          {submitting
            ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Enviando…</>
            : canAck
              ? <><CheckCircle2 className="w-5 h-5 mr-2" />Ciente</>
              : <><Lock className="w-5 h-5 mr-2" />Assista até o fim</>}
        </Button>
      </div>
      {/* Debug: currentTime helper only visible in dev consoles */}
      <span style={{ display: "none" }}>{currentTime}</span>
    </motion.div>
  );
}
